import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  Settings,
  BarChart3,
  FileText,
  UserCog,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Bell,
  CreditCard,
  Shield,
  Database,
  Activity,
  Calendar,
  Mail,
  Globe,
  Zap,
  HelpCircle,
  Search,
  Upload,
  Download,
  Archive,
  Monitor,
  Smartphone,
  Key,
  LogOut,
  UserPlus,
  Video,
  Plus,
  Trash2,
  CalendarDays,
  Newspaper,
  ClipboardList,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { useCurrentUser, useAppDispatch } from '@/hooks/redux';
import { useLogoutMutation } from '@/store/api/authApi';
import { logout as logoutAction } from '@/store/slices/authSlice';
import { UserRole } from '@/types';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  badge?: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  // User Management removed per request
  {
    name: 'Community',
    icon: UserPlus,
    children: [{ name: 'Join Requests', href: '/community/requests', icon: UserPlus }],
  },
  {
    name: 'Sessions',
    icon: Video,
    children: [
      { name: 'All Sessions', href: '/sessions', icon: Video },
      { name: 'Create Session', href: '/sessions/create', icon: Plus },
      { name: 'Trash', href: '/sessions/trash', icon: Trash2 },
    ],
  },
  {
    name: 'News',
    icon: Newspaper,
    href: '/news',
  },
  {
    name: 'Events',
    icon: CalendarDays,
    children: [
      { name: 'All Events', href: '/events', icon: CalendarDays },
      { name: 'Join Requests', href: '/events/join-requests', icon: ClipboardList },
      { name: 'Scan Ticket', href: '/events/verify', icon: Search },
    ],
  },
  { name: 'Change Password', href: '/change-password', icon: Key },
];

interface NavItemComponentProps {
  item: NavItem;
  isOpen: boolean;
  level?: number;
  onNavClick?: () => void;
  onToggle?: () => void;
}

function NavItemComponent({
  item,
  isOpen,
  level = 0,
  onNavClick,
  onToggle,
}: NavItemComponentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const user = useCurrentUser();
  const isAdmin = user?.role === UserRole.ADMIN;

  // Don't render admin-only items for non-admin users
  if (item.adminOnly && !isAdmin) {
    return null;
  }

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  // Auto-expand if has active child or parent is active
  React.useEffect(() => {
    if ((hasActiveChild || isActive) && isOpen) {
      setIsExpanded(true);
    }
  }, [hasActiveChild, isActive, isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      if (!isOpen && onToggle) {
        onToggle();
        setIsExpanded(true);
      } else {
        setIsExpanded(!isExpanded);
      }
    }
  };

  const Icon = item.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <div
          className={cn(
            'flex w-full items-center rounded-lg text-sm font-medium transition-colors',
            isActive || hasActiveChild
              ? 'text-primary font-semibold'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          )}
        >
          {/* If has href, make icon+label a NavLink, else a toggle button */}
          {item.href ? (
            <NavLink
              to={item.href}
              onClick={onNavClick}
              className={({ isActive: na }) =>
                cn(
                  'flex flex-1 items-center px-3 py-2 rounded-lg',
                  na ? 'text-primary font-semibold' : ''
                )
              }
              style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
              title={!isOpen ? item.name : undefined}
            >
              <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
              {isOpen && <span className="flex-1 text-left">{item.name}</span>}
            </NavLink>
          ) : (
            <button
              onClick={handleClick}
              className="flex flex-1 items-center px-3 py-2"
              style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
              title={!isOpen ? item.name : undefined}
            >
              <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
              {isOpen && <span className="flex-1 text-left">{item.name}</span>}
            </button>
          )}
          {/* Chevron toggle */}
          {isOpen && (
            <button
              onClick={handleClick}
              className="pr-3 py-2 shrink-0"
              aria-label="Toggle submenu"
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        {/* Submenu */}
        {hasChildren && isExpanded && isOpen && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <NavItemComponent
                key={child.name}
                item={child}
                isOpen={isOpen}
                level={level + 1}
                onNavClick={onNavClick}
                onToggle={onToggle}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Regular nav item
  return (
    <NavLink
      to={item.href!}
      onClick={onNavClick}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          !isOpen && 'justify-center'
        )
      }
      style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
      {isOpen && (
        <>
          <span>{item.name}</span>
          {item.badge && (
            <span className="ml-auto inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const user = useCurrentUser();
  const dispatch = useAppDispatch();
  const [logoutMutation] = useLogoutMutation();

  const handleLogout = async () => {
    try {
      await logoutMutation().unwrap();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      dispatch(logoutAction());
    }
  };

  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={onToggle} />}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col bg-card border-r transition-all duration-300 overflow-hidden',
          isOpen ? 'w-64 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className={cn('flex h-16 items-center border-b px-4', !isOpen && 'justify-center')}>
          {isOpen ? (
            <>
              <div className="flex items-center flex-1">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg overflow-hidden shrink-0">
                  <img src="/logo.svg" alt="Logo" className="w-8 h-8 object-contain" />
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <span className="text-lg font-semibold">OCCSSG Admin</span>
                  <div className="text-xs text-muted-foreground">Control Panel</div>
                </div>
              </div>
              {/* Toggle button - desktop only, open state */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggle}
                className="hidden lg:flex h-6 w-6 shrink-0 ml-2"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="hidden lg:flex h-8 w-8"
              title="Expand sidebar"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-2 overflow-y-auto">
          {navigation.map((item) => (
            <NavItemComponent
              key={item.name}
              item={item}
              isOpen={isOpen}
              onToggle={onToggle}
              onNavClick={() => {
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
            />
          ))}
        </nav>

        {/* User info */}
        {isOpen && user && (
          <div className="border-t p-4">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold">
                {user.name
                  ? user.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .substring(0, 2)
                      .toUpperCase()
                  : user.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {user.role.toLowerCase()} • {user.email}
                </p>
              </div>
            </div>

            {/* Quick actions */}
            <div className="mt-3 flex space-x-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={handleLogout}>
                <LogOut className="h-3 w-3 mr-1" />
                Log out
              </Button>
            </div>
          </div>
        )}

        {/* Collapsed user indicator */}
        {!isOpen && user && (
          <div className="border-t p-2">
            <div className="flex justify-center">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm">
                {user.name.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
